/**
 * Section: "Okos járat-adatok" (semantics). Every PassDesignSemantics key gets a control.
 * Seed-populated keys (the flight trio + departure facts) are [LIVE] — read-only
 * with the "Élő adat" badge; all other keys (incl. airlineLoungePlaceID lounge-on-
 * Maps, the flight-date/status keys with no live writer this phase, passengerName,
 * boardingGroup) are editable [TEMPLATE] inputs.
 *
 * Emits a DeepPartial<PassDesign> patch (mergePassDesign recurses objects) so a
 * single-key edit never clobbers sibling semantics. flightNumber is the one numeric
 * key; it is [LIVE] in the flight seed, so it never produces a write here.
 */
import type { DeepPartial, PassDesign, PassDesignSemantics } from '@insurwiz/wallet-core';

import { HelpText } from '../designer/HelpHint';
import { LiveBadge } from '../designer/LiveBadge';

import { liveSemanticsKeys } from './seedLiveKeys';

export interface SemanticsEditorProps {
  design: PassDesign;
  /** The sample seed — its present keys drive the [LIVE] read-only set. */
  seed: PassDesign;
  onChange: (patch: DeepPartial<PassDesign>) => void;
}

type StringKey = Exclude<keyof PassDesignSemantics, 'flightNumber'>;

const STRING_ROWS: ReadonlyArray<{ key: StringKey; label: string }> = [
  { key: 'airlineCode', label: 'Légitársaság kódja' },
  { key: 'originalDepartureDate', label: 'Eredeti indulás' },
  { key: 'currentDepartureDate', label: 'Aktuális indulás' },
  { key: 'originalBoardingDate', label: 'Eredeti beszállás' },
  { key: 'currentBoardingDate', label: 'Aktuális beszállás' },
  { key: 'departureGate', label: 'Indulási kapu' },
  { key: 'departureTerminal', label: 'Terminál' },
  { key: 'departureAirportCode', label: 'Indulási reptér kódja' },
  { key: 'destinationAirportCode', label: 'Érkezési reptér kódja' },
  { key: 'transitStatus', label: 'Járat állapota' },
  { key: 'airlineLoungePlaceID', label: 'Lounge helyazonosító (Maps)' },
  { key: 'passengerName', label: 'Utas neve' },
  { key: 'boardingGroup', label: 'Beszállási csoport' },
];

export function SemanticsEditor({ design, seed, onChange }: SemanticsEditorProps): JSX.Element {
  const liveKeys = new Set<keyof PassDesignSemantics>(liveSemanticsKeys(seed));
  const semantics = design.semantics ?? {};

  function setString(key: StringKey, value: string): void {
    onChange({ semantics: { [key]: value } });
  }

  const flightLive = liveKeys.has('flightNumber');

  return (
    <section className="card p-4 space-y-3" aria-label="Okos járat-adatok">
      <h3 className="text-sm font-semibold text-slate-800">Okos járat-adatok</h3>
      <HelpText>
        Rejtett adatok, amiket a Wallet a beszállókártya élő funkcióihoz használ (pl. kapuváltozás
        értesítés). A legtöbbet a járat tölti ki.
      </HelpText>

      {/* LiveBadge stays OUTSIDE the <label>: its HelpHint <button> would otherwise become
          the label's first labelable descendant and steal the implicit association from the
          input (see PassFieldRow / BarcodeEditor for the same idiom). */}
      <div className="text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <label htmlFor="semantics-flightNumber">Járatszám</label>
          {flightLive ? <LiveBadge /> : null}
        </div>
        <input
          id="semantics-flightNumber"
          aria-label="Járatszám"
          type="number"
          className="input mt-1 w-full"
          readOnly={flightLive}
          value={semantics.flightNumber ?? ''}
          onChange={
            flightLive
              ? undefined
              : (e) => {
                  const n = e.target.value.trim();
                  onChange(
                    n === '' ? { semantics: {} } : { semantics: { flightNumber: Number(n) } },
                  );
                }
          }
        />
      </div>

      {STRING_ROWS.map(({ key, label }) => {
        const isLive = liveKeys.has(key);
        const inputId = `semantics-${key}`;
        return (
          <div key={key} className="text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <label htmlFor={inputId}>{label}</label>
              {isLive ? <LiveBadge /> : null}
            </div>
            <input
              id={inputId}
              aria-label={label}
              type="text"
              className="input mt-1 w-full"
              readOnly={isLive}
              value={semantics[key] ?? ''}
              onChange={isLive ? undefined : (e) => setString(key, e.target.value)}
            />
          </div>
        );
      })}
    </section>
  );
}
